<script setup lang="ts">
import { ArrowUpRight, UserRound } from '@lucide/vue'
import { Handle, Position } from '@vue-flow/core'
import { ZhTooltip } from '@zerohuman/ui'
import { computed } from 'vue'
import { statusTone } from '@/lib/statusTone'
import { TASK_GRAPH_NODE_HEIGHT, TASK_GRAPH_NODE_WIDTH, type TaskGraphNode, UNASSIGNED_LABEL } from '@/lib/taskGraph'

const props = defineProps<{ data: TaskGraphNode; navigation?: 'task' | 'role' }>()
const emit = defineEmits<(event: 'activate') => void>()
const roleNavigation = computed(() => props.navigation === 'role' && props.data.offCanvas && Boolean(props.data.roleId))
const openable = computed(() => roleNavigation.value || props.data.taskId !== null)
const accessibleLabel = computed(() => {
    if (roleNavigation.value) return `Open role ${props.data.roleName || props.data.roleId}`
    return openable.value ? `Open task ${props.data.slug}` : undefined
})
/** A task runs as its role, never a member, so the card names the role rather than a person. */
const runsAs = computed(() => props.data.runsAs || UNASSIGNED_LABEL)
const lastRunStatus = computed(() => (props.data.offCanvas ? null : props.data.lastRunStatus))
/* BG §18.4 via @/lib/statusTone, so a task's last run reads the same colour as that run does everywhere else. */
const tone = computed(() => statusTone(lastRunStatus.value))

function onKeydown(event: KeyboardEvent) {
    if (!openable.value || !['Enter', ' ', 'Spacebar'].includes(event.key)) return
    event.preventDefault()
    emit('activate')
}
</script>

<template>
	<div
		class="task-node"
		:class="{ 'task-node-external': data.offCanvas, 'task-node-openable': openable }"
		:style="{ width: `${TASK_GRAPH_NODE_WIDTH}px`, height: `${TASK_GRAPH_NODE_HEIGHT}px` }"
		:role="roleNavigation ? 'link' : openable ? 'button' : undefined"
		:tabindex="openable ? 0 : undefined"
		:aria-label="accessibleLabel"
		@keydown="onKeydown"
	>
		<Handle type="target" :position="Position.Left" :connectable="false" />
		<div class="task-node-heading">
			<span class="task-node-icon" :class="{ 'task-node-icon-external': data.offCanvas }">
				<ArrowUpRight v-if="data.offCanvas" :size="18" aria-hidden="true" />
				<UserRound v-else :size="18" aria-hidden="true" />
			</span>
			<div class="task-node-title">
				<p>{{ data.name || data.slug }}</p>
				<span v-if="data.name && data.name !== data.slug">{{ data.slug }}</span>
			</div>
		</div>
		<p class="task-node-goal">{{ data.goal || (data.offCanvas ? 'This task belongs to another role or team.' : 'No goal specified.') }}</p>
		<div class="task-node-footer">
			<template v-if="data.offCanvas">
				<span class="task-node-scope">Outside this view</span>
				<ZhTooltip v-if="roleNavigation" :text="data.roleName"><span class="task-node-role">{{ data.roleName }}<ArrowUpRight :size="12" aria-hidden="true" /></span></ZhTooltip>
			</template>
			<template v-else>
				<ZhTooltip :text="`Runs as ${runsAs}`"><span class="task-node-assignee" data-testid="task-graph-node-runs-as">{{ runsAs }}</span></ZhTooltip>
				<ZhTooltip v-if="lastRunStatus" :text="`Last run: ${lastRunStatus}`">
					<span class="task-node-status" :data-tone="tone" data-testid="task-graph-node-status" :data-status="lastRunStatus">
						<i aria-hidden="true" />{{ lastRunStatus.replaceAll('_', ' ') }}
					</span>
				</ZhTooltip>
				<span v-else class="task-node-never">Never run</span>
			</template>
		</div>
		<Handle type="source" :position="Position.Right" :connectable="false" />
	</div>
</template>

<style scoped>
.task-node {
    display: flex;
    flex-direction: column;
    padding: 14px;
    border: 1.5px solid var(--border);
    border-radius: 0;
    background: var(--card);
    color: var(--card-foreground);
    box-shadow: 0 3px 8px #00000008, 0 1px 2px #00000005;
    cursor: pointer;
    transition: border-color .15s, box-shadow .15s;
}
.task-node:hover, .task-node:focus-visible {
    border-color: var(--electric-cyan);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--electric-cyan) 10%, transparent), 0 4px 12px #0000000a;
    outline: none;
}
.task-node-external {
    border-style: dashed;
    background: var(--muted);
    cursor: default;
    box-shadow: none;
}
.task-node-external:not(.task-node-openable):hover {
    border-color: var(--border);
    box-shadow: none;
}
.task-node-openable { cursor: pointer; }
.task-node-role { display: inline-flex; align-items: center; gap: 3px; min-width: 0; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--muted-foreground); }
.task-node-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.task-node-icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    flex: none;
    border-radius: 0;
    background: color-mix(in srgb, var(--electric-cyan) 8%, transparent);
    color: var(--electric-cyan);
}
.task-node-icon-external {
    background: var(--background);
    color: var(--muted-foreground);
}
.task-node-title {
    min-width: 0;
}
.task-node-title p {
    font-size: 13px;
    line-height: 16px;
    font-weight: 600;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.task-node-title span {
    display: block;
    font-size: 10px;
    line-height: 13px;
    color: var(--muted-foreground);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.task-node-goal {
    flex-shrink: 0;
    margin-top: 8px;
    color: var(--muted-foreground);
    font-size: 11px;
    line-height: 15px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.task-node-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: auto;
    padding-top: 7px;
    font-size: 11px;
    line-height: 14px;
}
.task-node-assignee {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.task-node-scope, .task-node-never {
    color: var(--muted-foreground);
}
.task-node-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--muted-foreground);
    white-space: nowrap;
}
.task-node-status i {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
}
.task-node-status[data-tone=completed] {
    color: var(--state-complete);
}
.task-node-status[data-tone=failed] {
    color: var(--state-failed);
}
.task-node-status[data-tone=running] {
    color: var(--state-active);
}
.task-node-status[data-tone=action] {
    color: var(--state-action);
}
.task-node-status[data-tone=neutral] {
    color: var(--state-inactive);
}
.task-node :deep(.vue-flow__handle) {
    width: 10px;
    height: 10px;
    border: 2px solid var(--muted-grey);
    background: var(--card);
}
</style>
